/**
 * qiblaChime.js
 * -------------
 * Airport-style two-tone chime synthesized entirely via the Web Audio API.
 * No external audio files, no download, no bundle bloat.
 *
 * Sound design:
 *   • Two soft sine notes: E5 (659.25 Hz) → A5 (880 Hz)
 *   • Gentle ADSR envelope so nothing "clicks" or is harsh
 *   • Master gain kept low (~0.18) so it's calming, not startling
 *   • Volume can be muted globally via localStorage
 *
 * Vibration:
 *   • Single 40 ms pulse via navigator.vibrate — fires on aligned trigger
 *   • No-op on desktop / browsers without the Vibration API
 *
 * Preferences (localStorage keys):
 *   • falak.qibla.muted  — 'true' | 'false'  (default: false / unmuted)
 */

const LS_MUTED_KEY = 'falak.qibla.muted';

export const isQiblaMuted = () => {
  try { return localStorage.getItem(LS_MUTED_KEY) === 'true'; }
  catch (_e) { return false; }
};

export const setQiblaMuted = (val) => {
  try { localStorage.setItem(LS_MUTED_KEY, val ? 'true' : 'false'); }
  catch (_e) { /* noop */ }
};

// A single AudioContext instance — created lazily on first play so we don't
// eagerly break autoplay policies before the user interacts.
let sharedCtx = null;

const getCtx = () => {
  if (sharedCtx) return sharedCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try { sharedCtx = new Ctor(); return sharedCtx; }
  catch (_e) { return null; }
};

/**
 * Play a single sine tone with a soft attack/release envelope.
 */
const playTone = (ctx, freq, startAt, duration, peak = 0.18) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);

  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.04);
  gain.gain.setValueAtTime(peak, startAt + duration * 0.55);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.05);
};

/**
 * Play the airport-style two-tone confirmation chime.
 * Does nothing if the user has muted the app, if AudioContext is
 * unavailable, or if the context is still in a suspended state and
 * cannot be resumed synchronously.
 */
export const playQiblaChime = () => {
  if (isQiblaMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    // resume() returns a promise — we don't await; if the browser refuses
    // (no user gesture yet) the tones just won't play, which is fine.
    ctx.resume().catch(() => {});
  }
  const t = ctx.currentTime + 0.02;
  playTone(ctx, 659.25, t,        0.22);           // E5
  playTone(ctx, 880.00, t + 0.28, 0.34, 0.16);     // A5, slightly quieter
};

/**
 * Fire a single brief haptic pulse.  Silently ignored on unsupported UAs.
 */
export const buzzQibla = () => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try { navigator.vibrate(40); } catch (_e) { /* noop */ }
};

/**
 * Convenience — trigger both effects together.
 */
export const signalQiblaAligned = () => {
  buzzQibla();
  playQiblaChime();
};
